import {
  addClientBreadcrumb,
  reportClientEvent,
} from "./lib/observability/client-error-reporter"
import {
  sanitizeUrl,
  sanitizeUrlSubstrings,
} from "./lib/observability/url-sanitizer"

declare global {
  interface Window {
    __CLIENT_ERROR_OBSERVER_INSTALLED__?: boolean
  }
}

function getReportableResourceError(target: HTMLElement) {
  if (target instanceof HTMLScriptElement) {
    return {
      tagName: target.tagName,
      url: sanitizeUrl(target.src),
      crossOrigin: target.crossOrigin,
    }
  }

  if (target instanceof HTMLLinkElement) {
    const rel = target.rel.toLowerCase()
    const as = target.as.toLowerCase()

    if (
      rel === "stylesheet" ||
      rel === "modulepreload" ||
      (rel === "preload" && ["script", "style", "font"].includes(as))
    ) {
      return {
        tagName: target.tagName,
        rel,
        as,
        url: sanitizeUrl(target.href),
        crossOrigin: target.crossOrigin,
        integrity: target.integrity,
      }
    }
  }

  return null
}

function getSourceInfo(filename: string) {
  if (!filename) return undefined

  try {
    const url = new URL(filename, window.location.origin)
    const chunkMatch = url.pathname.match(/\/_next\/static\/chunks\/(.+)$/)

    return {
      origin: url.origin,
      pathname: url.pathname,
      chunk: chunkMatch?.[1],
    }
  } catch {
    return {
      raw: sanitizeUrlSubstrings(filename),
    }
  }
}

if (
  typeof window !== "undefined" &&
  !window.__CLIENT_ERROR_OBSERVER_INSTALLED__
) {
  window.__CLIENT_ERROR_OBSERVER_INSTALLED__ = true

  addClientBreadcrumb({ type: "app.init" })

  document.addEventListener("visibilitychange", () => {
    addClientBreadcrumb({
      type: "document.visibilitychange",
      visibilityState: document.visibilityState,
    })
  })

  window.addEventListener("pageshow", (event) => {
    addClientBreadcrumb({
      type: "page.pageshow",
      persisted: event.persisted,
    })
  })

  window.addEventListener("pagehide", (event) => {
    addClientBreadcrumb({
      type: "page.pagehide",
      persisted: event.persisted,
    })
  })

  window.addEventListener("online", () => {
    addClientBreadcrumb({ type: "network.online" })
  })

  window.addEventListener("offline", () => {
    addClientBreadcrumb({ type: "network.offline" })
  })

  window.addEventListener(
    "error",
    (event) => {
      try {
        const target = event.target

        if (target instanceof HTMLElement) {
          const resourceError = getReportableResourceError(target)

          if (resourceError) {
            reportClientEvent("resource.error", resourceError)
          }
          return
        }

        reportClientEvent("window.error", {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          source: getSourceInfo(event.filename),
          error: event.error,
        })
      } catch {
        // no-op
      }
    },
    true,
  )

  window.addEventListener("unhandledrejection", (event) => {
    try {
      reportClientEvent("unhandledrejection", {
        reason: event.reason,
      })
    } catch {
      // no-op
    }
  })
}

export function onRouterTransitionStart(
  url: string,
  navigationType: "push" | "replace" | "traverse",
) {
  addClientBreadcrumb({
    type: "router.transition.start",
    url: sanitizeUrl(url),
    navigationType,
  })
}
